import React, { useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import InputField from 'ui/InputField';
import api from 'config/axiosConfig';
import { useFormLifecycle } from 'hooks/useFormLifecycle';
import { passwordRecoverySchema } from 'lib/validation/authSchemas';
import { getApiErrorMessage } from 'lib/errors/normalizeApiError';
import { apiRoutes } from "util/apiRoutes";
import { getAuthUiStrings, resolveAuthLocale } from "i18n/authUi";

/**
 * Stable form id used to pair the password-recovery form with its
 * footer Submit button so browser-native Enter-key submit fires the
 * form's `onSubmit` (and not the Cancel button).
 *
 * See `docs/architecture/forms-standard.md` section 2.
 */
export const PASSWORD_RECOVERY_FORM_ID = "auth-password-recovery-form";

/**
 * State that the parent (`AuthModal`) needs to render the
 * forgotPassword footer correctly:
 *   - submit button enabled/disabled
 *   - spinner-only loading visual while the request is in flight
 *   - swap from "Cancel + Reset password" to "Done" once the success
 *     panel is shown (the user has nothing left to do here, so a
 *     single primary action is the right affordance)
 */
export interface PasswordRecoveryState {
  isSubmitting: boolean;
  isButtonDisabled: boolean;
  isSuccess: boolean;
}

interface PasswordRecoveryProps {
  email: string;
  /**
   * Receives state changes so the parent can render the shared modal
   * footer (Cancel + Reset password / Done) without this component
   * having to render its own duplicate buttons. AuthModal wires this
   * to swap the footer when `isSuccess` flips true.
   */
  onStateChange?: (state: PasswordRecoveryState) => void;
}

const PasswordRecovery: React.FC<PasswordRecoveryProps> = ({ email, onStateChange }) => {
  const ui = getAuthUiStrings(resolveAuthLocale());
  const [emailOrUsername, setEmailOrUsername] = useState<string>(email);
  const [isResetSuccess, setIsResetSuccess] = useState(false);
  const lifecycle = useFormLifecycle({
    getValues: () => ({ emailOrUsername }),
    schema: passwordRecoverySchema,
    isReady: true,
  });

  // Mirror local state to the parent so AuthModal can render the
  // single shared footer (Cancel + Reset password / Done) without
  // duplicating buttons inside this component. The previous version
  // rendered its own Cancel + Reset password AND let AuthModal render
  // its own Cancel, producing the "two Cancel buttons" UX bug.
  useEffect(() => {
    if (!onStateChange) {
      return;
    }
    onStateChange({
      isSubmitting: lifecycle.isSubmitting,
      isButtonDisabled: lifecycle.isSubmitting || !lifecycle.canSubmit,
      isSuccess: isResetSuccess,
    });
  }, [lifecycle.isSubmitting, lifecycle.canSubmit, isResetSuccess, onStateChange]);

  const handlePasswordReset = async (e?: React.FormEvent) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    const issue = lifecycle.validateOrError();
    if (issue) {
      lifecycle.setError(issue);
      return;
    }
    lifecycle.beginSubmit();
    try {
      await api.post(apiRoutes.auth.passwordForgot, { emailOrUsername });
      setIsResetSuccess(true);
    } catch (err) {
      // The new `getApiErrorMessage` prefers the backend's response
      // payload over axios's "Request failed with status code N"
      // technical default, so what the user sees here is the actual
      // user-facing message from the server (or the localized
      // fallback if the server didn't provide one).
      lifecycle.setError(getApiErrorMessage(err, ui.emailVerificationResendFailed));
    } finally {
      lifecycle.endSubmit();
    }
  };

  return (
    <div className="flex flex-col h-full max-h-full sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
      {isResetSuccess ? (
        <div className="flex flex-col flex-grow space-y-4">
          <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {ui.passwordResetSentSuccessBody}
          </p>

          <p className="text-eoe-text-meta dark:text-eoe-dark-text-meta text-sm">
            {ui.passwordResetSpamPrompt}{" "}
            <button
              type="button"
              onClick={() => handlePasswordReset()}
              className="font-semibold underline"
            >
              {ui.passwordResetResendLink}
            </button>
            .
          </p>

          {/*
            The primary "Done" action is rendered by AuthModal's
            footer (FormActions) so we keep one button, in one place,
            consistent with all other auth views. AuthModal swaps its
            footer from "Cancel + Reset password" to "Done" when this
            component flips `isSuccess: true` via `onStateChange`.
          */}
        </div>
      ) : (
        <div className="flex flex-col flex-grow space-y-4">
          <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {ui.passwordResetForgotIntro}
          </p>

          <form
            id={PASSWORD_RECOVERY_FORM_ID}
            onSubmit={handlePasswordReset}
            className="flex flex-col flex-grow space-y-4"
            noValidate
          >
            <div className="form-control mb-0">
              <InputField
                type="email"
                id="emailOrUsername"
                name="emailOrUsername"
                labelText={ui.emailOrUsernameLabel}
                fieldRequired
                value={emailOrUsername}
                onChange={(e) => setEmailOrUsername(e.target.value)}
                validationState={emailOrUsername.length >= 2 ? 'valid' : emailOrUsername ? 'invalid' : 'none'}
                spellCheck={false}
              />
            </div>

            {lifecycle.error && (
              <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm" role="alert">
                <Info className="mr-1 size-4" />
                <span>{lifecycle.error}</span>
              </div>
            )}
          </form>

          {/*
            Footer (Cancel + Reset password) is owned by AuthModal so
            we don't render duplicate buttons inside this component.
          */}
        </div>
      )}
    </div>
  );
};

export default PasswordRecovery;
